import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './Account.module.scss';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { updateProfile, signOut } from 'firebase/auth';

interface Business {
  id: string;
  name: string;
  website?: string;
}

const Account: React.FC = () => {
  const [firebaseUser] = useAuthState(auth);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', company: '' });

  const [password, setPassword] = useState('');
  const [passwordMsg, setPasswordMsg] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const [avatar, setAvatar] = useState('');
  const [avatarMsg, setAvatarMsg] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Business management state
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [bizForm, setBizForm] = useState({ name: '', website: '' });
  const [editingBizId, setEditingBizId] = useState<string | null>(null);
  const [bizError, setBizError] = useState('');
  const [bizLoading, setBizLoading] = useState(false);

  // -------- Load profile (from users/{uid}) + init name/avatar --------
  const loadProfile = useCallback(async () => {
    if (!firebaseUser) return;
    setAvatar(firebaseUser.photoURL || '');

    try {
      const ref = doc(db, 'users', firebaseUser.uid);
      const snap = await getDoc(ref);
      const company = (snap.exists() ? (snap.data() as any).company : '') || '';
      setForm({
        name: firebaseUser.displayName || '',
        company,
      });
    } catch {
      // ignore
    }
  }, [firebaseUser]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  // ---------- BUSINESSES ----------
  useEffect(() => {
    async function fetchBusinesses() {
      if (!firebaseUser) return;
      try {
        const token = await firebaseUser.getIdToken(true);
        const res = await fetch('/api/my-businesses', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          console.error('GET /api/my-businesses failed', res.status);
          return;
        }
        const data = await res.json();
        setBusinesses(data);
      } catch (e) {
        console.error('fetchBusinesses error', e);
      }
    }
    fetchBusinesses();
  }, [firebaseUser]);

  const handleBizSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!firebaseUser) return;
    if (!bizForm.name.trim() || !bizForm.website.trim()) {
      setBizError('Veuillez remplir tous les champs.');
      return;
    }
    setBizLoading(true);
    setBizError('');
    try {
      const token = await firebaseUser.getIdToken(true);
      let res: Response;

      if (editingBizId) {
        res = await fetch(`/api/my-businesses/${editingBizId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(bizForm),
        });
      } else {
        res = await fetch('/api/my-businesses', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(bizForm),
        });
      }

      if (res.status === 409) {
        setBizError('Nom déjà utilisé par un autre établissement.');
        setBizLoading(false);
        return;
      }
      if (!res.ok) {
        const txt = await res.text();
        console.error('Save business failed', res.status, txt);
        setBizError('Erreur lors de la sauvegarde.');
        setBizLoading(false);
        return;
      }

      setBizForm({ name: '', website: '' });
      setEditingBizId(null);

      const refresh = await fetch('/api/my-businesses', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await refresh.json();
      setBusinesses(data);
    } catch (e) {
      console.error('handleBizSave error', e);
      setBizError('Erreur lors de la sauvegarde.');
    }
    setBizLoading(false);
  };

  const handleBizEdit = (biz: Business) => {
    setEditingBizId(biz.id);
    setBizForm({ name: biz.name, website: biz.website || '' });
    setBizError('');
  };

  const handleBizDelete = async (id: string) => {
    if (!firebaseUser) return;
    if (!window.confirm('Supprimer cet établissement ?')) return;
    setBizLoading(true);
    try {
      const token = await firebaseUser.getIdToken(true);
      const res = await fetch(`/api/my-businesses/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const txt = await res.text();
        console.error('DELETE business failed', res.status, txt);
        setBizError('Erreur lors de la suppression.');
      } else {
        setBusinesses((prev) => prev.filter((b) => b.id !== id));
      }
    } catch (e) {
      console.error('handleBizDelete error', e);
      setBizError('Erreur lors de la suppression.');
    }
    setBizLoading(false);
  };

  // ---------- USER INFO ----------
  const handleEdit = () => setEditing(true);

  const handleCancel = () => {
    setEditing(false);
    setError('');
    loadProfile();
  };

  const handleSave = async () => {
    if (!firebaseUser) return;
    if (!form.name.trim() || !form.company.trim()) {
      setError('Veuillez remplir tous les champs.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await setDoc(
        doc(db, 'users', firebaseUser.uid),
        { company: form.company.trim() },
        { merge: true }
      );
      if ((firebaseUser.displayName || '') !== form.name.trim()) {
        await updateProfile(firebaseUser, { displayName: form.name.trim() });
      }
      await loadProfile();
      setEditing(false);
    } catch (e) {
      console.error('handleSave error', e);
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordMsg('Veuillez entrer un mot de passe.');
      return;
    }
    setPasswordMsg('Mot de passe changé ! (mock)');
    setPassword('');
    setTimeout(() => setPasswordMsg(''), 2000);
  };

  // ---------- DELETE ACCOUNT (real) ----------
  const handleDelete = async () => {
    if (!firebaseUser) return;
    try {
      setLoading(true);
      const token = await firebaseUser.getIdToken(true);

      const res = await fetch('/api/account?deleteAuth=1', {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      console.log('DELETE /api/account response', res.status, data);

      // Always sign out locally so the UI doesn't resurrect the cached user
      await signOut(auth);
      try { localStorage.clear(); sessionStorage.clear(); } catch {}

      setDeleted(true);

      // Hard redirect (prevents any stale state from showing)
      window.location.replace('/login?deleted=1');
    } catch (e) {
      console.error('Account deletion failed', e);
      alert('Suppression du compte: échec. Voir la console.');
      setLoading(false);
    }
  };

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAvatar(ev.target?.result as string);
        setAvatarMsg('Photo de profil mise à jour ! (mock)');
        setTimeout(() => setAvatarMsg(''), 2000);
      };
      reader.readAsDataURL(file);
    }
  };

  if (deleted) {
    return (
      <main className={styles.accountContainer}>
        <h1 className={styles.accountDeleted}>Compte supprimé</h1>
        <p className={styles.accountDeleted}>Votre compte a été supprimé.</p>
      </main>
    );
  }

  return (
    <main className={styles.accountContainer}>
      <h1>Mon Compte</h1>

      <div className={styles.accountCard}>
        <button
          onClick={handleEdit}
          className={styles.accountEditBtn}
          aria-label="Modifier"
          style={{ display: editing ? 'none' : undefined }}
        >
          Modifier
        </button>

        <h2 className={styles.accountSectionTitle}>Informations</h2>

        <div className={styles.accountFlexRow}>
          <div className={styles.accountAvatarBox}>
            <img
              src={
                avatar ||
                firebaseUser?.photoURL ||
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  form.name || firebaseUser?.email || 'User'
                )}`
              }
              alt="Avatar"
              className={styles.accountAvatar}
              tabIndex={0}
              aria-label="Photo de profil"
              onClick={() => fileInputRef.current?.click()}
            />
            <button
              type="button"
              className={styles.accountAvatarBtn}
              onClick={() => fileInputRef.current?.click()}
              aria-label="Changer la photo de profil"
            >
              Changer la photo
            </button>
            <input
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              ref={fileInputRef}
              className={styles.accountAvatarInput}
              tabIndex={-1}
            />
            {avatarMsg && (
              <div className={styles.accountFeedback} aria-live="polite" role="status">
                ✔️ {avatarMsg}
              </div>
            )}
          </div>

          <div className={styles.accountInfoCol}>
            <div className={styles.accountField}>
              <span className={styles.accountLabel}>Nom :</span>
              {editing ? (
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={styles.accountInput}
                  aria-label="Nom"
                  autoFocus
                />
              ) : (
                <span className={styles.accountFieldValue}>{form.name}</span>
              )}
            </div>

            <div className={styles.accountField}>
              <span className={styles.accountLabel}>Email :</span>
              <span className={styles.accountFieldValue}>{firebaseUser?.email || ''}</span>
            </div>

            <div className={styles.accountField}>
              <span className={styles.accountLabel}>Entreprise :</span>
              {editing ? (
                <input
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                  className={styles.accountInput}
                  aria-label="Entreprise"
                />
              ) : (
                <span className={styles.accountFieldValue}>{form.company || '—'}</span>
              )}
            </div>

            {error && (
              <div className={styles.accountError} aria-live="assertive" role="alert">
                ❌ {error}
              </div>
            )}

            {editing && (
              <div className={styles.accountActionsRow}>
                <button
                  onClick={handleSave}
                  className={`${styles.accountButton} ${styles.accountButtonSmall}`}
                  disabled={loading}
                  aria-label="Enregistrer"
                >
                  Enregistrer
                  {loading && <span className={styles.accountButtonLoading} />}
                </button>
                <button
                  onClick={handleCancel}
                  className={`${styles.accountButton} ${styles.accountButtonSmall} ${styles.accountButtonCancel}`}
                  aria-label="Annuler"
                >
                  Annuler
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <hr className={styles.accountDivider} />

      {/* Businesses card */}
      <div className={styles.accountCard}>
        <h2 className={styles.accountSectionTitle}>Mes établissements</h2>
        <form onSubmit={handleBizSave} className={styles.accountFormRow} noValidate>
          <input
            type="text"
            value={bizForm.name}
            onChange={(e) => setBizForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Nom de l'établissement"
            className={styles.accountInput}
            aria-label="Nom de l'établissement"
            required
          />
          <input
            type="url"
            value={bizForm.website}
            onChange={(e) => setBizForm((f) => ({ ...f, website: e.target.value }))}
            placeholder="Site web (https://...)"
            className={styles.accountInput}
            aria-label="Site web"
            required
          />
          <div className={styles.accountActionsRow}>
            <button type="submit" className={styles.accountButton} disabled={bizLoading}>
              {editingBizId ? 'Enregistrer' : 'Ajouter'}
            </button>
            {editingBizId && (
              <button
                type="button"
                className={`${styles.accountButton} ${styles.accountButtonCancel}`}
                onClick={() => {
                  setEditingBizId(null);
                  setBizForm({ name: '', website: '' });
                  setBizError('');
                }}
              >
                Annuler
              </button>
            )}
          </div>
        </form>

        {bizError && (
          <div className={styles.accountError} aria-live="assertive" role="alert">
            {bizError}
          </div>
        )}

        <ul className={styles.accountBizList}>
          {businesses.map((biz) => (
            <li key={biz.id} className={styles.accountBizItem}>
              <div className={styles.accountBizText}>
                <span className={styles.accountBizName}>{biz.name}</span>
                <span className={styles.accountBizWebsite}>{biz.website}</span>
              </div>
              <div className={styles.accountBizActions}>
                <button
                  className={`${styles.accountButton} ${styles.accountButtonSmall}`}
                  onClick={() => handleBizEdit(biz)}
                  disabled={bizLoading}
                >
                  Modifier
                </button>
                <button
                  className={`${styles.accountButton} ${styles.accountButtonSmall} ${styles.accountButtonDelete}`}
                  onClick={() => handleBizDelete(biz.id)}
                  disabled={bizLoading}
                >
                  Supprimer
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <hr className={styles.accountDivider} />

      <div className={styles.accountCard}>
        <h2 className={styles.accountSectionTitle}>Changer le mot de passe</h2>
        <form onSubmit={handlePasswordChange} className={styles.accountFormRow}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nouveau mot de passe"
            className={styles.accountInput}
            aria-label="Nouveau mot de passe"
          />
          <div className={styles.accountActionsRow}>
            <button type="submit" className={styles.accountButton} aria-label="Changer le mot de passe">
              Changer
            </button>
          </div>
        </form>
        {passwordMsg && (
          <div
            className={password.trim() ? styles.accountFeedback : styles.accountError}
            aria-live="polite"
            role="status"
          >
            {password.trim() ? '✔️' : '❌'} {passwordMsg}
          </div>
        )}
      </div>

      <hr className={styles.accountDivider} />

      <div className={styles.accountCard}>
        <h2 className={styles.accountSectionTitle}>Supprimer le compte</h2>
        {deleteConfirm ? (
          <div className={styles.accountActionsRow}>
            <p className={styles.accountDeleteWarning}>
              Êtes-vous sûr de vouloir supprimer votre compte ? Cette action est irréversible.
            </p>
            <div className={styles.accountActionsRow}>
              <button
                onClick={handleDelete}
                className={`${styles.accountButton} ${styles.accountButtonDelete}`}
                disabled={loading}
                aria-label="Confirmer la suppression"
              >
                Confirmer
                {loading && <span className={styles.accountButtonLoading} />}
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                className={`${styles.accountButton} ${styles.accountButtonCancel}`}
                aria-label="Annuler la suppression"
              >
                Annuler
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setDeleteConfirm(true)}
            className={`${styles.accountButton} ${styles.accountButtonDelete}`}
            aria-label="Supprimer mon compte"
          >
            Supprimer mon compte
          </button>
        )}
      </div>
    </main>
  );
};

export default Account;
